import type {
  AgentBuilderResponse,
  AgentGraph,
  AgentNode,
  AgentNodeContext,
  AgentNodeDecision,
  AgentNodeManaged,
  AgentNodeRules,
  AgentNodeTask,
  AgentRulesCondition,
  AgentRulesConditionGroup,
  AgentTaskNode,
  JsonSchemaObject,
  ManagedTemplate,
  MimeType,
  DecisionOption as WireDecisionOption,
  DecisionQuestion as WireDecisionQuestion,
  DecisionStateRef as WireDecisionStateRef,
} from '@onefootprint/request-types/dashboard';
import { isBlankKey } from './field-key';
import type {
  Artifact,
  ArtifactType,
  Bundle,
  BundleNode,
  DecisionLevel,
  DecisionNode,
  DecisionOption,
  DecisionQuestion,
  DecisionStateRef,
  Field,
  FieldType,
  InputField,
  ManagedNode,
  ManagedNodeKind,
  Property,
  RuleCondition,
  RuleGroup,
  RulesNode,
  SessionMeta,
  Task,
} from './types';
import { isDecisionNode, isManagedNode, isTaskNode } from './types';
import { uid } from './utils';

type AgentInput = AgentGraph['inputs'][number];
type AgentArtifact = AgentNodeTask['artifacts'][number];
type AgentJudgmentField = AgentNodeTask['fields'][number];

export const sessionFromResponse = (r: AgentBuilderResponse): SessionMeta => ({
  agentId: r.agentId,
  agentVersionId: r.current.id,
  versionTag: r.current.versionTag,
});

const isTaskWireNode = (n: AgentNode): n is AgentNodeTask => n.kind === 'task';
const isRulesWireNode = (n: AgentNode): n is AgentNodeRules => n.kind === 'rules';
const isManagedWireNode = (n: AgentNode): n is AgentNodeManaged => n.kind === 'managed';
const isDecisionWireNode = (n: AgentNode): n is AgentNodeDecision => n.kind === 'decision';

export const agentInputToWire = (input: InputField): AgentInput => {
  const { type } = input;
  return {
    fieldName: input.name,
    comment: input.description || undefined,
    isRequired: input.required,
    schema: type === null ? undefined : schemaEditorStateToSchema({ ...input, type }),
  };
};

export const inputsToWire = (inputs: InputField[]): AgentInput[] => inputs.map(agentInputToWire);

export type { AgentInput };

export const agentInputFromWire = (wire: AgentInput): InputField => {
  const id = uid('input');
  const schemaState = schemaToSchemaEditorState(wire.schema, id);
  return {
    id,
    name: wire.fieldName,
    description: wire.comment ?? '',
    // An old backend (or a clipboard payload from one) omits `isRequired`; match the server's
    // default rather than reading the absence as optional.
    required: wire.isRequired ?? true,
    ...schemaState,
    type: wire.schema ? schemaState.type : null,
  };
};

const toSchemaEnum = (values: string[]): JsonSchemaObject['enum'] => values as unknown as JsonSchemaObject['enum'];

const fromSchemaEnum = (schema: JsonSchemaObject | undefined): string[] | undefined =>
  schema?.enum ? (schema.enum as unknown as string[]).filter(v => typeof v === 'string') : undefined;

const fromSchemaCriteria = (schema: JsonSchemaObject | undefined): Record<string, string> | undefined => {
  const written = (schema as SchemaWithExtras | undefined)?.['x-criteria'];
  return written && Object.keys(written).length > 0 ? written : undefined;
};

const ARTIFACT_TYPE_TO_MIME: Record<ArtifactType, MimeType> = {
  markdown: 'markdown',
  pdf: 'pdf',
  csv: 'csv',
};

export const MIME_TO_ARTIFACT_TYPE: Partial<Record<MimeType, ArtifactType>> = {
  markdown: 'markdown',
  pdf: 'pdf',
  csv: 'csv',
};

/**
 * `x-criteria` is a vendor key, and provisional: it keeps per-value criteria through this round trip
 * so the editor is usable, but the backend may not echo an unrecognised key. The standards-compliant
 * home is `oneOf: [{ const, description }]`, which is the shape to agree before this ships.
 */
type SchemaWithExtras = JsonSchemaObject & { description?: string; 'x-criteria'?: Record<string, string> };
export type SchemaEditorState = Pick<Field, 'enumValues' | 'criteria' | 'children'> & { type: FieldType | null };

// A patch from the schema editor only ever sets a concrete type; null is reserved
// for legacy inputs loaded without a schema and can't be chosen in the editor.
export type SchemaPatch = Partial<Pick<Field, 'type' | 'enumValues' | 'criteria' | 'children'>>;

// Project the schema-editing slice out of a Field / InputField for the shared editor controls.
export const toSchemaEditorState = ({
  type,
  enumValues,
  criteria,
  children,
}: SchemaEditorState): SchemaEditorState => ({
  type,
  enumValues,
  criteria,
  children,
});

const namedChildren = (children: Property[]): Property[] => children.filter(c => !isBlankKey(c.name));

const childrenToObjectSchema = (children: Property[] | undefined): SchemaWithExtras => {
  const named = namedChildren(children ?? []);
  return {
    type: 'object',
    properties: Object.fromEntries(named.map(child => [child.name, propertyToSchema(child)])),
    required: named.map(child => child.name),
  };
};

const propertyToSchema = (p: Property): SchemaWithExtras =>
  fieldTypeToSchema({
    type: p.type,
    enumValues: p.enumValues,
    children: p.children,
    description: p.description,
    criteria: p.criteria,
  });

type SchemaShape = {
  type: FieldType;
  enumValues?: string[];
  children?: Property[];
  description?: string;
  criteria?: Record<string, string>;
};

const fieldTypeToSchema = ({ type, enumValues, children, description, criteria }: SchemaShape): SchemaWithExtras => {
  const base: SchemaWithExtras = description ? { description } : {};
  if (type === 'document') return { ...base, type: 'string', format: 'document' };
  // Criteria describe enum values, so they ride at the field's own level whether it holds one value or
  // an array of them — and only there. The editor keeps them through a type change so changing it back
  // restores them, but a `text` schema carrying criteria describes values that do not exist.
  const describesValues = type === 'enum' || type === 'array<enum>';
  if (describesValues && criteria && Object.keys(criteria).length > 0) base['x-criteria'] = criteria;
  if (type === 'number') return { ...base, type: 'number' };
  if (type === 'integer') return { ...base, type: 'integer' };
  if (type === 'boolean') return { ...base, type: 'boolean' };
  if (type === 'enum') return { ...base, type: 'string', enum: toSchemaEnum(enumValues ?? []) };
  if (type === 'object') return { ...base, ...childrenToObjectSchema(children) };
  if (type === 'array<object>') {
    return {
      ...base,
      type: 'array',
      items: childrenToObjectSchema(children),
    };
  }
  if (type === 'array<string>') {
    return { ...base, type: 'array', items: { type: 'string' } };
  }
  if (type === 'array<enum>') {
    return {
      ...base,
      type: 'array',
      items: { type: 'string', enum: toSchemaEnum(enumValues ?? []) },
    };
  }
  return { ...base, type: 'string' };
};

export const schemaToFieldType = (schema: JsonSchemaObject | undefined): FieldType => {
  if (!schema) return 'text';
  if (schema.type === 'string' && schema.format === 'document') return 'document';
  if (schema.type === 'number') return 'number';
  if (schema.type === 'integer') return 'integer';
  if (schema.type === 'boolean') return 'boolean';
  if (schema.enum && schema.enum.length > 0) return 'enum';
  if (schema.type === 'object') return 'object';
  if (schema.type === 'array') {
    const items = schema.items;
    if (items?.enum && items.enum.length > 0) return 'array<enum>';
    if (items?.type === 'object') return 'array<object>';
    return 'array<string>';
  }
  return 'text';
};

const schemaToSchemaEditorState = (
  schema: JsonSchemaObject | undefined,
  parentPath: string,
): SchemaEditorState & { type: FieldType } => {
  const type = schemaToFieldType(schema);
  let enumValues: string[] | undefined;
  if (type === 'enum') enumValues = fromSchemaEnum(schema);
  if (type === 'array<enum>') enumValues = fromSchemaEnum(schema?.items);
  let children: Property[] | undefined;
  if (type === 'object') children = propertiesFromSchema(schema, parentPath);
  if (type === 'array<object>') children = propertiesFromSchema(schema?.items, parentPath);
  return { type, enumValues, criteria: fromSchemaCriteria(schema), children };
};

const schemaEditorStateToSchema = (state: SchemaEditorState & { type: FieldType }): JsonSchemaObject =>
  fieldTypeToSchema({
    type: state.type,
    enumValues: state.enumValues,
    children: state.children,
    criteria: state.criteria,
  });

/**
 * `required` is the record of the order the author wrote: the backend holds `properties` in a `HashMap`,
 * so its key order comes back arbitrary, while an array survives. Keys it omits follow, as they arrived.
 */
const propertiesFromSchema = (schema: JsonSchemaObject | undefined, parentPath: string): Property[] => {
  const props = (schema?.properties ?? {}) as Record<string, unknown>;
  // De-duplicated: a `required` naming one key twice would build that property into two rows, the
  // second reading as a duplicate of the first.
  const declared = [...new Set(schema?.required ?? [])].filter(key => key in props);
  const rest = Object.keys(props).filter(key => !declared.includes(key));
  return [...declared, ...rest].map(key => schemaToProperty(key, props[key] as SchemaWithExtras, parentPath));
};

const schemaToProperty = (key: string, schema: SchemaWithExtras, parentPath: string): Property => {
  const type = schemaToFieldType(schema);
  const description = schema.description;
  const id = parentPath ? `${parentPath}.${key}` : key;
  const base: Property = { id, name: key, type, description };
  if (type === 'enum') return { ...base, enumValues: fromSchemaEnum(schema), criteria: fromSchemaCriteria(schema) };
  if (type === 'array<enum>')
    return { ...base, enumValues: fromSchemaEnum(schema.items), criteria: fromSchemaCriteria(schema) };
  if (type === 'object') return { ...base, children: propertiesFromSchema(schema, id) };
  if (type === 'array<object>') return { ...base, children: propertiesFromSchema(schema.items, id) };
  return base;
};

const fieldToWire = (field: Field): AgentJudgmentField => ({
  key: field.name,
  prompt: field.prompt,
  schema: schemaEditorStateToSchema(field),
});

const artifactToWire = (artifact: Artifact): AgentArtifact => ({
  // The key is identity, not a label: `update_task_artifact` names an artifact by it and
  // `artifactFromWire` rebuilds `id` from it. Derived from the editable name, a rename moved it out
  // from under both.
  key: artifact.id,
  name: artifact.name,
  prompt: artifact.spec,
  mimeType: ARTIFACT_TYPE_TO_MIME[artifact.type],
  isStarred: artifact.isStarred,
});

export const fieldFromWire = (wire: AgentJudgmentField): Field => {
  const schemaState = schemaToSchemaEditorState(wire.schema, wire.key);
  return {
    id: wire.key,
    name: wire.key,
    ...schemaState,
    prompt: wire.prompt ?? '',
  };
};

export const artifactFromWire = (wire: AgentArtifact): Artifact => ({
  id: wire.key,
  name: wire.name,
  type: MIME_TO_ARTIFACT_TYPE[wire.mimeType] ?? 'markdown',
  spec: wire.prompt,
  isStarred: wire.isStarred ?? false,
});

const taskFromWire = (node: AgentTaskNode): Task => ({
  kind: 'task',
  id: node.id,
  name: node.name,
  prompt: node.prompt,
  fields: node.fields.map(fieldFromWire),
  artifacts: node.artifacts.map(artifactFromWire),
});

export const taskNodeForWire = (task: Task): AgentNodeTask => ({
  id: task.id,
  kind: 'task',
  name: task.name,
  prompt: task.prompt,
  fields: task.fields.filter(f => !isBlankKey(f.name)).map(fieldToWire),
  artifacts: task.artifacts.map(artifactToWire),
});

const conditionFromWire = (wire: AgentRulesCondition): RuleCondition => ({
  id: wire.id,
  expression: wire.expression,
});

const conditionToWire = (c: RuleCondition): AgentRulesCondition => ({
  id: c.id,
  expression: c.expression,
});

const groupFromWire = (wire: AgentRulesConditionGroup): RuleGroup => ({
  id: wire.groupId,
  value: wire.value,
  conditions: wire.conditions.map(conditionFromWire),
});

const groupToWire = (g: RuleGroup): AgentRulesConditionGroup => ({
  groupId: g.id,
  value: g.value,
  conditions: g.conditions.map(conditionToWire),
});

const rulesNodeFromWire = (node: AgentNodeRules): RulesNode => ({
  kind: 'rules',
  id: node.id,
  name: node.name,
  key: node.key,
  defaultValue: node.defaultValue,
  groups: node.groups.map(groupFromWire),
});

const rulesNodeForWire = (rulesNode: RulesNode): AgentNodeRules => ({
  id: rulesNode.id,
  kind: 'rules',
  name: rulesNode.name,
  key: rulesNode.key,
  defaultValue: rulesNode.defaultValue,
  groups: rulesNode.groups.map(groupToWire),
});

const DEFAULT_THRESHOLD = 0.5;

// A probability typed as text; anything outside 0..1 (or not a number) reads as unset.
export const parseProbability = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : undefined;
};

const stateRefFromWire = (wire: WireDecisionStateRef): DecisionStateRef => ({
  id: uid('sref'),
  source: wire.source,
  key: wire.key,
});

const stateRefToWire = (ref: DecisionStateRef): WireDecisionStateRef =>
  ref.source === 'input' ? { source: 'input', key: ref.key } : { source: 'output', key: ref.key };

const optionFromWire = (wire: WireDecisionOption): DecisionOption => ({
  id: uid('opt'),
  value: wire.value,
  description: wire.description ?? '',
});

const optionToWire = (option: DecisionOption): WireDecisionOption => ({
  value: option.value,
  description: option.description || undefined,
});

const levelFromWire = (description: string): DecisionLevel => ({ id: uid('lvl'), description });

const questionFromWire = (wire: WireDecisionQuestion): DecisionQuestion => {
  const base = { id: uid('q'), key: wire.key, prompt: wire.prompt };
  switch (wire.kind) {
    case 'yes_no':
      return {
        ...base,
        kind: 'yes_no',
        yesMeans: wire.yesMeans ?? '',
        noMeans: wire.noMeans ?? '',
        threshold: String(wire.threshold ?? DEFAULT_THRESHOLD),
      };
    case 'pick_one':
      return {
        ...base,
        kind: 'pick_one',
        options: wire.options.map(optionFromWire),
      };
    case 'score':
      return { ...base, kind: 'score', levels: wire.levels.map(levelFromWire) };
  }
};

const questionToWire = (question: DecisionQuestion): WireDecisionQuestion => {
  const base = { key: question.key, prompt: question.prompt };
  switch (question.kind) {
    case 'yes_no':
      return {
        ...base,
        kind: 'yes_no',
        yesMeans: question.yesMeans || undefined,
        noMeans: question.noMeans || undefined,
        threshold: parseProbability(question.threshold) ?? DEFAULT_THRESHOLD,
      };
    case 'pick_one':
      return {
        ...base,
        kind: 'pick_one',
        options: question.options.map(optionToWire),
      };
    case 'score':
      return { ...base, kind: 'score', levels: question.levels.map(level => level.description) };
  }
};

const decisionNodeFromWire = (node: AgentNodeDecision): DecisionNode => ({
  kind: 'decision',
  id: node.id,
  name: node.name,
  context: node.context,
  state: node.state.map(stateRefFromWire),
  questions: node.questions.map(questionFromWire),
  model: node.model ?? '',
});

const decisionNodeForWire = (node: DecisionNode): AgentNodeDecision => ({
  id: node.id,
  kind: 'decision',
  name: node.name,
  context: node.context,
  state: node.state.map(stateRefToWire),
  questions: node.questions.map(questionToWire),
  model: node.model.trim() || undefined,
});

const emptyTemplate = (kind: ManagedNodeKind): ManagedTemplate => ({
  kind,
  title: '',
  prompt: '',
  instructions: [],
  sections: [],
  inputs: [],
  requiredTools: [],
});

// The server supplies a nodeContext entry per managed node; these bare shells only guard a
// (contract-breaking) missing entry so the node still renders instead of vanishing.
const emptyTask = (id: string): Task => ({ kind: 'task', id, name: '', prompt: '', fields: [], artifacts: [] });

export const managedFromWire = (node: AgentNodeManaged, info?: AgentNodeContext): ManagedNode => ({
  kind: 'managed',
  id: node.id,
  managedKind: node.managedKind,
  config: node.data.config,
  version: node.data.version,
  task: info ? taskFromWire(info.task) : emptyTask(node.id),
  template: info?.template ?? emptyTemplate(node.managedKind),
});

// `config` is opaque (`Record<string, unknown>`) on the FE, so narrowing to the discriminated wire union is an assertion.
export const managedNodeForWire = (node: ManagedNode): AgentNodeManaged =>
  ({
    kind: 'managed',
    id: node.id,
    managedKind: node.managedKind,
    data: { config: node.config, version: node.version },
  }) as AgentNodeManaged;

export const nodeToWire = (node: BundleNode): AgentNode => {
  if (isTaskNode(node)) return taskNodeForWire(node);
  if (isManagedNode(node)) return managedNodeForWire(node);
  if (isDecisionNode(node)) return decisionNodeForWire(node);
  return rulesNodeForWire(node);
};

const nodeFromWire = (node: AgentNode): BundleNode | null => {
  if (isTaskWireNode(node)) return taskFromWire(node);
  if (isRulesWireNode(node)) return rulesNodeFromWire(node);
  if (isManagedWireNode(node)) return managedFromWire(node);
  if (isDecisionWireNode(node)) return decisionNodeFromWire(node);
  return null;
};

export const bundleFromResponse = (r: AgentBuilderResponse): Bundle => {
  const contextById = new Map((r.nodeContext ?? []).map(c => [c.nodeId, c]));
  return {
    name: r.name,
    icon: r.icon,
    norms: r.graph.norms ?? '',
    inputs: r.graph.inputs?.map(agentInputFromWire) ?? [],
    includeAllVaultDocuments: r.graph.includeAllVaultDocuments ?? false,
    nodes: r.graph.nodes.flatMap((n): BundleNode[] => {
      const node = nodeFromWire(n);
      if (!node) return [];
      if (isManagedNode(node)) {
        const info = contextById.get(node.id);
        return [info ? { ...node, task: taskFromWire(info.task), template: info.template } : node];
      }
      return [node];
    }),
    skills: r.graph.skills ?? [],
    tools: r.graph.tools ?? [],
    modelConfig: r.graph.modelConfig,
    outputs: r.graph.outputs,
    // `operation` is optional on the wire — BE marks legacy/incompatible suggestions
    // by stripping it. Drop those so the FE selector can assume operation is present.
    suggestions: (r.suggestions ?? []).flatMap(s =>
      s.operation ? [{ id: s.id, canApply: s.canApply, operation: s.operation, description: s.description }] : [],
    ),
  };
};

export const bundleToGraph = (bundle: Bundle): AgentGraph => ({
  norms: bundle.norms,
  inputs: inputsToWire(bundle.inputs),
  includeAllVaultDocuments: bundle.includeAllVaultDocuments ?? false,
  nodes: bundle.nodes.map(nodeToWire),
  skills: bundle.skills,
  tools: bundle.tools,
  modelConfig: bundle.modelConfig,
  outputs: bundle.outputs ?? {},
});
